import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import axios from '../../../library/axios';
import { MySwal } from '../../../library/swal';
import {
  handleJobQueuedSuccess,
  handleJobRequestError,
} from '../../../helpers/cpanelJobFeedback';
import CpanelPageShell from '../../../components/cpanel/CpanelPageShell';
import ActionBarCpanelUsers from '../../../components/moleculs/cpanel/users/action-bar';
import CreateCpanelUserComp from '../../../components/moleculs/cpanel/users/create';
import DatatableCpanelUsers from '../../../components/moleculs/cpanel/users/datatable';
import EditCpanelUserComp from '../../../components/moleculs/cpanel/users/edit';
import ShowCpanelUserComp from '../../../components/moleculs/cpanel/users/show';
import ImportCpanelUserComp from '../../../components/moleculs/cpanel/users/import';
import ExportCpanelUserComp from '../../../components/moleculs/cpanel/users/export';
import { USER_SCOPE_CENTRAL, USER_SCOPE_HOSPITAL } from '../../../components/moleculs/cpanel/users/constants';

const CpanelUsersPage = () => {
  const [activeTab, setActiveTab] = useState(USER_SCOPE_HOSPITAL);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantSlug, setTenantSlug] = useState('');
  const [hospitalMeta, setHospitalMeta] = useState({ groups: [], divisions: [] });
  const [editUser, setEditUser] = useState(null);
  const [showUser, setShowUser] = useState(null);
  const [lgShowAdd, setLgShowAdd] = useState(false);
  const [lgShowDetail, setLgShowDetail] = useState(false);
  const [lgShowImport, setLgShowImport] = useState(false);
  const [lgShowExport, setLgShowExport] = useState(false);
  const [lgShowUpdate, setLgShowUpdate] = useState(false);

  const scope = activeTab;
  const isHospital = scope === USER_SCOPE_HOSPITAL;
  const hospitalReady = true;
  const [formTenantSlug, setFormTenantSlug] = useState('');

  const fetchTenants = useCallback(async () => {
    try {
      const { data } = await axios.get('/cpanel/users/tenants');
      setTenants(Array.isArray(data) ? data : []);
    } catch {
      setTenants([]);
    }
  }, []);

  const fetchHospitalMeta = useCallback(async (slug) => {
    if (!slug) {
      setHospitalMeta({ groups: [], divisions: [] });
      return;
    }
    try {
      const { data } = await axios.get('/cpanel/users/meta', { params: { tenantSlug: slug } });
      setHospitalMeta(data || { groups: [], divisions: [] });
    } catch {
      setHospitalMeta({ groups: [], divisions: [] });
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const params = { scope };
      if (isHospital && tenantSlug) params.tenantSlug = tenantSlug;
      const { data } = await axios.get('/cpanel/users/list', { params });
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setUsers([]);
      MySwal.fire({
        icon: 'error',
        title: 'Gagal memuat data',
        text: err?.response?.data?.message || 'Tidak dapat mengambil daftar pengguna.',
      });
    }
  }, [scope, isHospital, tenantSlug]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);


  useEffect(() => {
    if (isHospital && tenantSlug) {
      fetchHospitalMeta(tenantSlug);
    }
  }, [isHospital, tenantSlug, fetchHospitalMeta]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filterUsers = useMemo(() => {
    const keyword = search.toLowerCase();
    return users.filter((item) => {
      const fields = [
        item.username,
        item.namaLengkap,
        item.fullName,
        item.email,
        item.role,
        item.groupName,
        item.tenantSlug,
        item.tenantDescription,
        item.groupName,
        item.divisionName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return fields.includes(keyword);
    });
  }, [users, search]);

  const runDeleteUser = async (row) => {
    const confirm = await MySwal.fire({
      title: 'Hapus pengguna ini?',
      text: `@${row.username}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, hapus',
      cancelButtonText: 'Batal',
    });
    if (!confirm.isConfirmed) return;

    try {
      const params = { scope };
      if (isHospital) params.tenantSlug = row.tenantSlug || tenantSlug;
      const response = await axios.delete(`/cpanel/users/delete/${row.id}`, { params });
      await handleJobQueuedSuccess({
        response,
        actionLabel: 'Penghapusan pengguna',
        fetch: fetchUsers,
      });
    } catch (err) {
      handleJobRequestError(err, 'Gagal memulai penghapusan pengguna.');
    }
  };

  const modalTitlePrefix = isHospital ? 'Rumah Sakit' : 'CPanel Center';

  return (
    <>
      <CpanelPageShell
        title="User"
        subtitle="Kelola pengguna CPanel Center dan pengguna rumah sakit beserta role"
        actions={
          <ActionBarCpanelUsers
            showFormAdd={() => {
              setFormTenantSlug(tenantSlug || '');
              if (tenantSlug) fetchHospitalMeta(tenantSlug);
              setLgShowAdd(true);
            }}
            showFormImport={() => setLgShowImport(true)}
            showFormExport={() => setLgShowExport(true)}
            disabled={!hospitalReady}
          />
        }
      >
        <ul className="nav nav-tabs cpanel-notifications-page__tabs" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              type="button"
              className={`cpanel-notifications-page__tab${activeTab === USER_SCOPE_CENTRAL ? ' cpanel-notifications-page__tab--active' : ''}`}
              onClick={() => {
                setActiveTab(USER_SCOPE_CENTRAL);
                setSearch('');
                setLgShowAdd(false);
                setLgShowImport(false);
                setLgShowExport(false);
              }}
            >
              Pengguna CPanel Center
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              type="button"
              className={`cpanel-notifications-page__tab${activeTab === USER_SCOPE_HOSPITAL ? ' cpanel-notifications-page__tab--active' : ''}`}
              onClick={() => {
                setActiveTab(USER_SCOPE_HOSPITAL);
                setSearch('');
                setLgShowAdd(false);
                setLgShowImport(false);
                setLgShowExport(false);
              }}
            >
              Pengguna Rumah Sakit
            </button>
          </li>
        </ul>

        {isHospital ? (
          <div className="mb-4" style={{ maxWidth: 420 }}>
            <label className="form-label">Filter tenant</label>
            <select
              className="form-select form-select-sm"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
            >
              <option value="">Semua tenant</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.slug}>
                  {t.slug} - {t.description}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="card">
          <div className="card-body cpanel-tenants-card-body">
            <DatatableCpanelUsers
              scope={scope}
              listUsers={filterUsers}
              search={search}
              handleChangeSearch={setSearch}
              handleShowUser={(row) => {
                setShowUser(row);
                setLgShowDetail(true);
              }}
              handleEditUser={(row) => {
                setEditUser(row);
                setFormTenantSlug(row.tenantSlug || tenantSlug);
                if (row.tenantSlug) fetchHospitalMeta(row.tenantSlug);
                setLgShowUpdate(true);
              }}
              handleHapusUser={runDeleteUser}
            />
          </div>
        </div>
      </CpanelPageShell>

      <Modal show={lgShowAdd} onHide={() => setLgShowAdd(false)} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Tambah Pengguna {modalTitlePrefix}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateCpanelUserComp
            key={`create-${scope}-${formTenantSlug || tenantSlug}`}
            scope={scope}
            tenantSlug={formTenantSlug || tenantSlug}
            tenants={tenants}
            meta={hospitalMeta}
            close={() => setLgShowAdd(false)}
            fetch={fetchUsers}
            onTenantChange={(slug) => {
              setFormTenantSlug(slug);
              if (slug) fetchHospitalMeta(slug);
            }}
          />
        </Modal.Body>
      </Modal>

      <Modal show={lgShowImport} onHide={() => setLgShowImport(false)} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Impor Pengguna {modalTitlePrefix}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImportCpanelUserComp
            key={`import-${scope}-${tenantSlug}`}
            scope={scope}
            tenantSlug={tenantSlug}
            tenants={tenants}
            close={() => setLgShowImport(false)}
            fetch={fetchUsers}
          />
        </Modal.Body>
      </Modal>

      <Modal show={lgShowExport} onHide={() => setLgShowExport(false)} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Ekspor Pengguna {modalTitlePrefix}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ExportCpanelUserComp
            key={`export-${scope}-${tenantSlug}`}
            scope={scope}
            tenantSlug={tenantSlug}
            close={() => setLgShowExport(false)}
          />
        </Modal.Body>
      </Modal>

      <Modal show={lgShowDetail} onHide={() => setLgShowDetail(false)} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Detail Pengguna</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ShowCpanelUserComp scope={scope} user={showUser} />
        </Modal.Body>
      </Modal>

      <Modal show={lgShowUpdate} onHide={() => setLgShowUpdate(false)} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Pengguna {modalTitlePrefix}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EditCpanelUserComp
            scope={scope}
            tenantSlug={formTenantSlug || editUser?.tenantSlug || tenantSlug}
            tenants={tenants}
            meta={hospitalMeta}
            editUser={editUser}
            onTenantChange={(slug) => {
              setFormTenantSlug(slug);
              if (slug) fetchHospitalMeta(slug);
            }}
            close={() => setLgShowUpdate(false)}
            fetch={fetchUsers}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CpanelUsersPage;
